/**
 * Canvas data saving utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import { convertImageToDataUrl } from "./imageProcessingUtils";
import { convertVideoToDataUrl, generateVideoThumbnail } from "./exportUtils";
import { deduplicate } from "./navigationBoundsUtils";
import { dedupeCanvasTextsByEntity } from "./canvasTextEntityUtils";
import {
  preserveTemplateStructure,
  hydrateLineArrowPointsForPersist,
} from "./canvasStateUtils";
import { isMarkerId } from "../../../util/shapeTypeMapper";
import { persistCanvasPages } from "../../SectionElements/plugins/utils";
import { createEmptyCanvasPage } from "./canvasPageUtils";

const persistLineLikeElements = (arr) =>
  deduplicate(
    (preserveTemplateStructure(arr || []) || []).map((el) =>
      hydrateLineArrowPointsForPersist(el),
    ),
  );

/**
 * Prepares and saves canvas data to IndexedDB and localStorage
 * @param {Object} params - Parameters for saving canvas data
 * @param {string|null} params.snapshot - Canvas snapshot as data URL
 * @param {Array} params.images - Current images array
 * @param {Array} params.videos - Current videos array
 * @param {Array} params.texts - Current texts array
 * @param {Array} params.rectangles - Current rectangles array
 * @param {Array} params.circles - Current circles array
 * @param {Array} params.arrows - Current arrows array
 * @param {Array} params.lines - Current lines array
 * @param {Array} params.doubleSidedArrows - Current double-sided arrows array
 * @param {Array} params.scribbles - Current scribbles array
 * @param {string} params.canvasHeight - Current canvas height
 * @param {string} params.pageId - Current page ID
 * @param {boolean} params.fromCanvasTemplate - Whether this is from canvas template
 * @param {Function} params.setCanvasPages - Function to set canvas pages state
 * @param {Function} params.setHasUnsavedChanges - Function to set unsaved changes flag
 * @param {Function} params.saveCanvasElementsToIndexedDB - Function to save to IndexedDB
 * @param {string[]} [params.validTextShapeIds] - API Text shape_ids only (exclude image text_boxes); persist so MyCanvas filter shows correct 2 texts
 * @returns {Promise<Object>} Promise resolving to {elementCounts, canvasData}
 */
export async function prepareAndSaveCanvasData({
  snapshot,
  images,
  videos,
  texts,
  rectangles,
  circles,
  arrows,
  lines,
  doubleSidedArrows,
  scribbles,
  offCanvasTexts,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasArrows,
  offCanvasLines,
  offCanvasDoubleSidedArrows,
  offCanvasScribbles,
  offCanvasImages,
  offCanvasVideos,
  canvasHeight,
  pageId,
  fromCanvasTemplate,
  setCanvasPages,
  setHasUnsavedChanges,
  saveCanvasElementsToIndexedDB,
  validTextShapeIds,
}) {
  // Process images - MATCH API FORMAT with image_properties structure.
  // If conversion throws (e.g. tainted canvas), keep minimal serializable form so save still runs.
  // Same pipeline for main and off-canvas so IndexedDB never JPEG-encodes live Image nodes (loses alpha).
  const processCanvasImageForPersist = (img) => {
    try {
      const existingCroppedDataUrl =
        img.cropData &&
        img.imageDataUrl &&
        typeof img.imageDataUrl === "string" &&
        img.imageDataUrl.startsWith("data:")
          ? img.imageDataUrl
          : null;
      const imageDataUrl =
        existingCroppedDataUrl || convertImageToDataUrl(img.image);
      const originalImageDataUrl =
        img.originalImageDataUrl &&
        typeof img.originalImageDataUrl === "string" &&
        img.originalImageDataUrl.startsWith("data:")
          ? img.originalImageDataUrl
          : convertImageToDataUrl(img.originalImage);
      const uncompressedCroppedImageDataUrl =
        img.uncompressedCroppedImageDataUrl &&
        typeof img.uncompressedCroppedImageDataUrl === "string" &&
        img.uncompressedCroppedImageDataUrl.startsWith("data:")
          ? img.uncompressedCroppedImageDataUrl
          : convertImageToDataUrl(img.uncompressedCroppedImage);
      return {
        ...img,
        id: img.id || img.image_id,
        x: img.x || 0,
        y: img.y || 0,
        width: img.width || 100,
        height: img.height || 100,
        image: null,
        originalImage: null,
        compressedImage: null,
        uncompressedCroppedImage: null,
        src: imageDataUrl || img.src,
        imageDataUrl,
        originalImageDataUrl,
        uncompressedCroppedImageDataUrl,
        ...(img.cropData && { cropData: img.cropData }),
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        ...(img.flipHorizontal !== undefined && { flipHorizontal: img.flipHorizontal }),
        ...(img.flipVertical !== undefined && { flipVertical: img.flipVertical }),
        ...(img.originalCropRotation !== undefined && { originalCropRotation: img.originalCropRotation }),
        ...(img.hasBakedTransforms !== undefined && { hasBakedTransforms: img.hasBakedTransforms }),
        rotation: img.rotation || 0,
        scaleX: img.scaleX || 1,
        scaleY: img.scaleY || 1,
      };
    } catch (e) {
      const keepDataUrl = (url) =>
        url && typeof url === "string" && url.startsWith("data:");
      const savedImageDataUrl = keepDataUrl(img.imageDataUrl)
        ? img.imageDataUrl
        : (img.imageDataUrl || img.src || "");
      const savedOriginalDataUrl = keepDataUrl(img.originalImageDataUrl)
        ? img.originalImageDataUrl
        : (img.originalImageDataUrl || "");
      const savedUncompressedCropped = keepDataUrl(img.uncompressedCroppedImageDataUrl)
        ? img.uncompressedCroppedImageDataUrl
        : (img.uncompressedCroppedImageDataUrl || "");
      return {
        ...img,
        id: img.id || img.image_id,
        x: img.x || 0,
        y: img.y || 0,
        width: img.width || 100,
        height: img.height || 100,
        image: null,
        originalImage: null,
        compressedImage: null,
        uncompressedCroppedImage: null,
        src: savedImageDataUrl || img.src || "",
        imageDataUrl: savedImageDataUrl,
        originalImageDataUrl: savedOriginalDataUrl,
        uncompressedCroppedImageDataUrl: savedUncompressedCropped,
        ...(img.cropData && { cropData: img.cropData }),
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        rotation: img.rotation || 0,
        scaleX: img.scaleX || 1,
        scaleY: img.scaleY || 1,
      };
    }
  };

  const processedImages = images?.map(processCanvasImageForPersist) || [];
  const processedOffCanvasImages =
    (preserveTemplateStructure(offCanvasImages || []) || []).map(
      processCanvasImageForPersist,
    );

  // Process videos - convert blob URLs to data URLs. On failure keep minimal data so save still runs.
  const processedVideos = await Promise.all(
    (videos || []).map(async (video) => {
      try {
        const videoSrc = await convertVideoToDataUrl(video.src);
        const thumbnail = await generateVideoThumbnail(
          videoSrc,
          video.width || 320,
          video.height || 180
        );
        return {
          ...video,
          x: video.x || 0,
          y: video.y || 0,
          width: video.width || 320,
          height: video.height || 180,
          src: videoSrc,
          videoDataUrl: videoSrc,
          thumbnail: thumbnail,
        };
      } catch (e) {
        return {
          ...video,
          x: video.x || 0,
          y: video.y || 0,
          width: video.width || 320,
          height: video.height || 180,
          src: video.src || "",
          videoDataUrl: video.videoDataUrl || video.src || "",
          thumbnail: video.thumbnail || null,
        };
      }
    })
  );

  // Texts: dedupe by shape_id / inner id / text_box_id (navigation dedupe() only keys on id)
  let textsForSave = dedupeCanvasTextsByEntity(preserveTemplateStructure(texts));
  // Ensure user-added texts (marker ids) are marked so they're included in payload in handleConfirmSave.
  // createdOnCanvas can be lost if Leave is clicked before state flushes.
  textsForSave = textsForSave.map((t) => {
    if (!t) return t;
    const hasMarkerId = isMarkerId(t.id) || isMarkerId(t.shape_id);
    if (hasMarkerId && t.createdOnCanvas !== true) {
      return { ...t, createdOnCanvas: true };
    }
    return t;
  });

  const canvasData = {
    texts: textsForSave,
    images: deduplicate(processedImages),
    videos: deduplicate(processedVideos),
    rectangles: deduplicate(preserveTemplateStructure(rectangles)),
    circles: deduplicate(preserveTemplateStructure(circles)),
    arrows: persistLineLikeElements(arrows),
    scribbles: deduplicate(preserveTemplateStructure(scribbles)),
    lines: persistLineLikeElements(lines),
    doubleSidedArrows: persistLineLikeElements(doubleSidedArrows),
    snapshot,
    canvasHeight: parseInt(canvasHeight),
    ...(Array.isArray(validTextShapeIds) && validTextShapeIds.length > 0 ? { validTextShapeIds } : {}),
  };

  const offCanvasData = {
    texts: dedupeCanvasTextsByEntity(
      preserveTemplateStructure(offCanvasTexts || [])
    ),
    images: deduplicate(processedOffCanvasImages),
    videos: deduplicate(preserveTemplateStructure(offCanvasVideos || [])),
    rectangles: deduplicate(
      preserveTemplateStructure(offCanvasRectangles || [])
    ),
    circles: deduplicate(preserveTemplateStructure(offCanvasCircles || [])),
    arrows: persistLineLikeElements(offCanvasArrows),
    scribbles: deduplicate(preserveTemplateStructure(offCanvasScribbles || [])),
    lines: persistLineLikeElements(offCanvasLines),
    doubleSidedArrows: persistLineLikeElements(offCanvasDoubleSidedArrows),
  };

  // Debug template structure preservation
  if (canvasData.texts?.length > 0) {
    const hasTemplateElements =
      texts.some((t) => t.shape_info) ||
      canvasData.texts.some((t) => t.shape_info);
    if (hasTemplateElements) {
    }
  }

  // Update canvas pages
  setCanvasPages((prevPages) => {
    const updated = (prevPages || []).map((p) =>
      p?.id === pageId
        ? {
            ...p,
            ...canvasData,
            elements: canvasData,
            lastModified: new Date().toISOString(),
            canvasHeight: parseInt(canvasHeight),
          }
        : p
    );

    const final = updated.length > 0 ? updated : [createEmptyCanvasPage()];

    // Persist metadata to localStorage (skip when coming from Canvas Template to avoid quota issues).
    // Do NOT include snapshot - it causes QuotaExceededError; snapshots are stored in IndexedDB only.
    if (!fromCanvasTemplate) {
      try {
        const pagesToPersist = final.map((p) => ({
          id: p?.id || p?.pageId,
          lastModified: p?.lastModified || new Date().toISOString(),
          newlyAdded: p?.newlyAdded,
          canvasHeight: p?.canvasHeight ?? 100,
          zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
          text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
          text_box_content_data: Array.isArray(p?.text_box_content_data)
            ? p.text_box_content_data
            : [],
        }));
        persistCanvasPages(pagesToPersist);
      } catch (e) {
      }
    }
    return final;
  });

  // Save to IndexedDB (critical - must run so new elements persist when returning to MyCanvas)
  try {
    // User-added texts: explicitly createdOnCanvas, or have marker id (e.g. pasted/created without flag) and not from template
    const hasMarkerId = (t) =>
      (t?.id && String(t.id).includes("marker")) ||
      (t?.shape_id && String(t.shape_id).includes("marker"));
    const isFromTemplate = (t) =>
      t?.isFromTemplate === true || t?.fromCanvasTemplate === true;
    const userTexts = (canvasData?.texts || []).filter(
      (t) =>
        t.createdOnCanvas === true ||
        (hasMarkerId(t) && !isFromTemplate(t))
    );
    await saveCanvasElementsToIndexedDB({
      data: canvasData,
      pageId,
      storeName: "canvasElements",
    });
    await saveCanvasElementsToIndexedDB({
      data: offCanvasData,
      pageId,
      storeName: "offCanvasElements",
    });
  } catch (err) {
    console.error("[SAVE-UTILS] IndexedDB save failed:", err?.message ?? err);
    throw err;
  }

  // Update session storage
  const elementCounts = {
    texts: texts?.length || 0,
    images: processedImages.length,
    videos: processedVideos.length,
    rectangles: rectangles?.length || 0,
    circles: circles?.length || 0,
    arrows: arrows?.length || 0,
    lines: lines?.length || 0,
    doubleSidedArrows: doubleSidedArrows?.length || 0,
    scribbles: scribbles?.length || 0,
  };

  sessionStorage.setItem(
    "canvasStateInfo",
    JSON.stringify({
      hasChanges: true,
      lastModified: new Date().toISOString(),
      snapshot,
      pageId,
      elementCounts,
    })
  );

  setHasUnsavedChanges(false);
  return { elementCounts, canvasData };
}
